const VALID_DECODED_TOKENS = new Set<string>(["barclays_pre-arrears"]);

export const BARCLAYS_MM_LENDER_NAME = "Barclays Money Management";

const safeAtob = (value: string): string | null => {
  try {
    // `atob` is available in browsers; avoid pulling node Buffer polyfills.
    return atob(value);
  } catch {
    return null;
  }
};

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getHashQuery = (hash: string): URLSearchParams => {
  const idx = hash.indexOf("?");
  if (idx === -1) return new URLSearchParams();
  return new URLSearchParams(hash.slice(idx + 1));
};

export const extractCtTokenFromUrl = (): string | null => {
  if (typeof window === "undefined") return null;

  // HashRouter often yields URLs like `/#/?ct=...`.
  const fromSearch = new URLSearchParams(window.location.search).get("ct");
  if (fromSearch) return fromSearch;

  const fromHash = getHashQuery(window.location.hash).get("ct");
  if (fromHash) return fromHash;

  return null;
};

export const decodeCtToken = (ctToken: string): string | null => {
  if (!ctToken) return null;
  // Legacy does: atob(ctParam). For safety, also tolerate URI-encoded tokens.
  const candidate = safeDecodeURIComponent(ctToken);
  return safeAtob(candidate);
};

export const isValidCtToken = (ctToken: string): boolean => {
  const decoded = decodeCtToken(ctToken);
  return typeof decoded === "string" && VALID_DECODED_TOKENS.has(decoded);
};

export const getStoredCtToken = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("qsParam");
    return raw && raw.trim().length > 0 ? raw : null;
  } catch {
    return null;
  }
};

export const getStoredCtTokenIfValid = (): string | null => {
  const raw = getStoredCtToken();
  return raw && isValidCtToken(raw) ? raw : null;
};

export const persistCtIfValid = (): string | null => {
  if (typeof window === "undefined") return null;

  const ctToken = extractCtTokenFromUrl();
  if (!ctToken) return null;
  if (!isValidCtToken(ctToken)) return null;

  try {
    localStorage.setItem("qsParam", ctToken);
  } catch {
    // ignore storage failures
  }

  return ctToken;
};

export const getDecodedCustomerType = (): string | null => {
  const raw = getStoredCtTokenIfValid();
  if (!raw) return null;

  const decoded = decodeCtToken(raw);
  if (!decoded || !VALID_DECODED_TOKENS.has(decoded)) return null;

  return decoded;
};

export const isPrearrearsCustomer = (customerRecord: unknown): boolean => {
  // Backend-established truth
  if (customerRecord && typeof customerRecord === "object") {
    const ct = (customerRecord as { customerType?: unknown }).customerType;
    if (typeof ct === "string" && ct.trim().length > 0) {
      return true;
    }
  }

  // Fallback: token still present in browser storage
  return getDecodedCustomerType() !== null;
};



